import { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useScreenWidth } from '@hooks/useScreenWidth'
import { Drawer } from 'antd'
import { CloseIcon } from '@/utils/icons'
import s from './index.module.css'
import './style.css'
import { notificationApi } from '@/api'
import { useAddAlert } from '@/hooks/useAddAlert'
import websocket from '@/utils/websocket'
import moment from 'moment'
import { useChatContext } from '@/components/common/Chat/ChatContext'
import { useNotifications } from './useNotifications'
import { getNotificationPlainText } from '@/components/common/Chat/helpers/mentions'
import {
  buildCrmIntegrationMessagePath,
  getCrmIntegrationMessageProviderLabel,
  openCrmIntegrationMessageNotification,
} from './crmIntegrationMessageNotification'
import Button from '@UI/New/Button'

const getOriginHostname = (value) => {
  if (!value || typeof value !== 'string') return null
  try {
    return new URL(value).hostname
  } catch {
    return null
  }
}

const shouldSkipByOrigin = (checkForOrigin, notification, envelope) => {
  if (!checkForOrigin) return false

  const originHostname =
    getOriginHostname(notification?.origin) ||
    getOriginHostname(envelope?.origin)
  if (!originHostname) return false

  return originHostname !== window.location.hostname
}

const Notification = () => {
  const [open, setOpen] = useState(false)
  const {
    notifications,
    unreadCount,
    addNotification,
    setNotificationsFromServer,
    markAllAsRead,
  } = useNotifications()
  const isMobile = useScreenWidth(992)
  const width = isMobile ? '100vw' : '440px'
  const addAlert = useAddAlert()
  const chatContext = useChatContext()
  const history = useHistory()

  const onClose = () => {
    setOpen(false)
    markAllAsRead()
    notificationApi.markAsRead().catch((error) => console.error(error))
  }

  const showDrawer = () => {
    setOpen(true)
  }

  const fetchNotifications = () => {
    notificationApi
      .getNotifications()
      .then((response) => {
        const newNotifications = response?.data
        if (Array.isArray(newNotifications)) {
          setNotificationsFromServer(newNotifications)
        }
      })
      .catch((error) => console.error(error))
  }

  useEffect(() => {
    websocket.connect()

    const showNewNotificationAlerts = (newNotification, extra = {}) => {
      addAlert({
        title: newNotification.title,
        text: getNotificationPlainText(newNotification.message),
        sound: true,
        ...extra,
      })
    }

    const handler = (envelope, checkForOrigin = false) => {
      try {
        if (envelope?.notification?.type === 'chat.read') return

        const notificationData = envelope?.notification
        const notification =
          typeof notificationData === 'string'
            ? JSON.parse(notificationData)
            : notificationData

        if (!notification || typeof notification !== 'object') return

        if (shouldSkipByOrigin(checkForOrigin, notification, envelope)) return

        if (notification?.type === 'chat_new_message') {
          if (notification?.supportInternal) return
          if (
            chatContext?.currentOpenChatId &&
            String(notification?.chatId) ===
              String(chatContext.currentOpenChatId)
          ) {
            return
          }

          showNewNotificationAlerts(notification)
          return
        }

        if (
          notification?.type === 'crm_integration_message' ||
          notification?.type === 'crm_lead_status_changed' ||
          notification?.type === 'crm_lead_field_changed' ||
          notification?.type === 'new_lead'
        ) {
          addNotification(notification)
          showNewNotificationAlerts(notification, {
            onClick: () =>
              openCrmIntegrationMessageNotification(notification, history),
          })
          return
        }

        addNotification(notification)
        showNewNotificationAlerts(notification)
      } catch (error) {
        console.error('Failed to handle platform notification', error)
      }
    }

    const unsubscribeNotifications = websocket.subscribe(
      'notifications',
      (value) => handler(value, false),
    )
    const unsubscribeNotification = websocket.subscribe(
      'notification',
      (value) => handler(value, true),
    )

    fetchNotifications()

    return () => {
      unsubscribeNotifications?.()
      unsubscribeNotification?.()
    }
  }, [addAlert, addNotification, chatContext?.currentOpenChatId, history])

  const linkify = (text) => {
    if (!text) return ''

    const urlRegex = /(https?:\/\/[^\s]+)/g

    return text
      .replace(
        urlRegex,
        (url) =>
          `<a href="${url}" target="_blank" rel="noopener noreferrer" style="color: var(--color-link-blue);">${url}</a>`,
      )
      .replace(/\n/g, '<br>')
  }

  const formatDate = (value) => {
    if (!value) return ''

    const date = typeof value === 'number' ? moment.unix(value) : moment(value)
    return date.isValid() ? date.format('DD.MM.YYYY HH:mm') : ''
  }

  const handleNotificationClick = (notification) => {
    if (
      notification?.type !== 'crm_integration_message' &&
      notification?.type !== 'crm_lead_status_changed' &&
      notification?.type !== 'crm_lead_field_changed' &&
      notification?.type !== 'new_lead'
    ) {
      return
    }
    openCrmIntegrationMessageNotification(notification, history)
    onClose()
  }

  return (
    <>
      <button onClick={showDrawer} className={s.button} title="Уведомления">
        <div
          className={`${s.numberOfNotifications} ${
            unreadCount > 0 ? s.pulse : ''
          }`}
        >
          {unreadCount}
        </div>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M14.857 17.082C16.7202 16.8614 18.5509 16.4217 20.311 15.772C18.8204 14.1208 17.9967 11.9745 18 9.75V9C18 7.4087 17.3679 5.88258 16.2427 4.75736C15.1174 3.63214 13.5913 3 12 3C10.4087 3 8.8826 3.63214 7.75738 4.75736C6.63216 5.88258 6.00002 7.4087 6.00002 9V9.75C6.00304 11.9746 5.17901 14.121 3.68802 15.772C5.42102 16.412 7.24802 16.857 9.14302 17.082M14.857 17.082C12.959 17.3071 11.041 17.3071 9.14302 17.082M14.857 17.082C15.0011 17.5319 15.037 18.0094 14.9616 18.4757C14.8863 18.942 14.7019 19.384 14.4234 19.7656C14.145 20.1472 13.7803 20.4576 13.3592 20.6716C12.9381 20.8856 12.4724 20.9972 12 20.9972C11.5276 20.9972 11.0619 20.8856 10.6408 20.6716C10.2197 20.4576 9.85509 20.1472 9.57664 19.7656C9.29819 19.384 9.11379 18.942 9.03844 18.4757C8.96309 18.0094 8.99892 17.5319 9.14302 17.082M3.12402 7.5C3.40599 5.82497 4.15782 4.26444 5.29202 3M18.708 3C19.8422 4.26444 20.5941 5.82497 20.876 7.5"
            stroke="var(--color-text-main)"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      <Drawer
        placement="right"
        closable={false}
        open={open}
        className="antDrawerStyle2002"
        width={width}
      >
        <div className={s.title}>Уведомления</div>
        {notifications?.length ? (
          <ul className={s.notificationsList}>
            {notifications?.map((notification, i) => {
              const isCrmLeadNotification =
                notification?.type === 'crm_integration_message' ||
                notification?.type === 'crm_lead_status_changed' ||
                notification?.type === 'crm_lead_field_changed' ||
                notification?.type === 'new_lead'
              const canOpenLeadCard =
                isCrmLeadNotification &&
                Boolean(buildCrmIntegrationMessagePath(notification))
              const providerLabel =
                notification?.type === 'crm_integration_message'
                  ? getCrmIntegrationMessageProviderLabel(notification.provider)
                  : null

              return (
                <li
                  key={i}
                  className={`${s.notificationsListItem} ${
                    !notification.is_read ? s.unread : ''
                  } ${canOpenLeadCard ? s.clickable : ''}`}
                  onClick={() => {
                    if (!canOpenLeadCard) return
                    handleNotificationClick(notification)
                  }}
                  onKeyDown={(event) => {
                    if (!canOpenLeadCard) return
                    if (event.key !== 'Enter' && event.key !== ' ') return
                    event.preventDefault()
                    handleNotificationClick(notification)
                  }}
                  role={canOpenLeadCard ? 'button' : undefined}
                  tabIndex={canOpenLeadCard ? 0 : undefined}
                >
                  {!notification.is_read && <div className={s.unreadDot}></div>}
                  <div className={s.notificationsListItemTitle}>
                    {notification.title}
                    {providerLabel ? (
                      <span className={s.providerBadge}>{providerLabel}</span>
                    ) : null}
                  </div>
                  <div
                    className={s.notificationsListItemMessage}
                    dangerouslySetInnerHTML={{
                      __html: linkify(notification.message),
                    }}
                    onClick={(event) => {
                      if (
                        event.target instanceof Element &&
                        event.target.closest('a')
                      ) {
                        event.stopPropagation()
                      }
                    }}
                  />
                  {notification.invoice_url && (
                    <a
                      className={s.invoiceLink}
                      href={notification.invoice_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(event) => event.stopPropagation()}
                    >
                      Скачать PDF-счёт на оплату
                    </a>
                  )}
                  <div className={s.notificationsListItemFooter}>
                    {canOpenLeadCard ? (
                      <Button
                        type="button"
                        color="primary"
                        className={s.openLeadButton}
                        onClick={(event) => {
                          event.stopPropagation()
                          handleNotificationClick(notification)
                        }}
                      >
                        Открыть карточку
                      </Button>
                    ) : (
                      <span />
                    )}
                    <div className={s.notificationsListItemDate}>
                      {formatDate(notification.created_at)}
                    </div>
                  </div>
                </li>
              )
            })}
          </ul>
        ) : (
          <div className={s.noNotifications}>Нет новых уведомлений.</div>
        )}
        <div className={s.close} onClick={onClose}>
          <CloseIcon />
        </div>
      </Drawer>
    </>
  )
}

export default Notification
